import { Table, TableHead, TableRow, TableCell, TableBody, Typography, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AssessmentScoreLegend from 'components/common/AssessmentScoreLegend';
import { forwardRef } from 'react';
import { Fragment } from 'react';
import {
  AssessmentType,
  BenchmarkChecklist,
  Checklist,
  Classroom,
  StudentChecklistOutcome,
  UnitChecklist,
  WeeklyChecklist,
} from 'types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import AssessmentScore from 'components/common/AssessmentScore';
import { getCurriculumBenchmarkChecklist } from 'util/checklists-util';

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    section: {
      backgroundColor: 'white',
      marginTop: '-1px',
      marginBottom: theme.spacing(8),
      borderTop: `1px solid ${(theme.palette as any).grey[600]}`,
    },
    obsChecklist: {
      '& .MuiTableCell-root': {
        border: `1px solid ${(theme.palette as any).grey[600]}`,
        borderTopColor: 'transparent',
        padding: theme.spacing(2),
      },
    },
  };
});

interface TableProps {
  classroom: Classroom;
  curriculumId: number;
  checklist: Checklist;
  outcomes?: StudentChecklistOutcome[];
}

const PrintableTable = forwardRef<HTMLDivElement, TableProps>((props, ref) => {
  const classes = useStyles();

  const getChecklistTitle = (checklist: Checklist) => {
    switch (checklist.type) {
      case AssessmentType.Benchmark: {
        const curriculumChecklist = getCurriculumBenchmarkChecklist(
          checklist as BenchmarkChecklist,
          props.curriculumId
        );
        return t(`class.benchmarkPeriod-${curriculumChecklist.period}`);
      }
      case AssessmentType.Unit: {
        return (checklist as UnitChecklist).unit.title;
      }
      case AssessmentType.Week: {
        return `${(checklist as WeeklyChecklist).unit.title} - ${
          t('class.week') + ' ' + (checklist as WeeklyChecklist).weekNumber
        }`;
      }
      default:
        break;
    }
  };

  return (
    <div ref={ref} className={classes.section}>
      <Table className={classes.obsChecklist}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="h1">{getChecklistTitle(props.checklist)}</Typography>
            </TableCell>
            <TableCell>
              <AssessmentScoreLegend />
            </TableCell>
            {props.classroom.students?.map((student) => {
              return (
                <TableCell key={'student-' + student.id}>
                  {student.firstName + ' ' + student.lastName}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.checklist.checklistDomains.map((checklistDomain) => {
            return (
              <Fragment key={'domain-row-' + checklistDomain.domain.id}>
                <TableRow>
                  <TableCell
                    rowSpan={
                      props.checklist.checklistObservations.filter(
                        (v) => v.observation.domainId === checklistDomain.domain.id
                      ).length + 1
                    }
                  >
                    <Typography variant="h4">{checklistDomain.domain.title}</Typography>
                  </TableCell>
                </TableRow>
                {props.checklist.checklistObservations
                  .filter((v) => v.observation.domainId === checklistDomain.domain.id)
                  .map((checklistObservation) => {
                    return (
                      <TableRow key={'observation-row-' + checklistObservation.observation.id}>
                        <TableCell>
                          <Typography variant="body1">
                            {checklistObservation.observation.title}
                          </Typography>
                        </TableCell>
                        {props.classroom.students?.map((student) => {
                          return (
                            <TableCell key={'student-observation-cell-' + student.id}>
                              <AssessmentScore
                                score={
                                  props.outcomes?.find(
                                    (v) =>
                                      v.studentId === student.id &&
                                      v.observationId === checklistObservation.observation.id
                                  )?.score
                                }
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
});

export default PrintableTable;
